import { useEffect, useMemo, useState, createElement } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Search, ChevronDown, ChevronRight, Users, LayoutGrid, List } from "lucide-react";
import { Link } from "react-router-dom";

const DEPT_COLORS = {
  "Engineering": "#DC2626",
  "Infrastructure & Cloud": "#0EA5E9",
  "Human Resources": "#8B5CF6",
  "Sales": "#F59E0B",
  "Presales": "#10B981",
  "Finance": "#6366F1",
  "Support & NOC": "#EC4899",
};
function deptColor(d) { return DEPT_COLORS[d] || "#64748B"; }

function initials(name = "") { return name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase(); }

// Flatten tree to indented rows so no recursive JSX component is needed
function flatten(nodes, level, out) {
  for (const n of nodes) {
    out.push({ node: n, level });
    if (n.reports && n.reports.length) flatten(n.reports, level + 1, out);
  }
  return out;
}

function nameOfDescendants(node, out) {
  out.add(node.name);
  (node.reports || []).forEach(c => nameOfDescendants(c, out));
  return out;
}

export default function Hierarchy() {
  const [tree, setTree] = useState([]);
  const [total, setTotal] = useState(0);
  const [collapsed, setCollapsed] = useState(new Set());
  const [q, setQ] = useState("");

  const [view, setView] = useState("list");
  useEffect(() => {
    api.get("/hierarchy").then(r => {
      setTree(r.data.tree);
      setTotal(r.data.total);
      // start with everything below level 1 collapsed
      const initial = new Set();
      const walk = (nodes, depth) => {
        nodes.forEach(n => {
          if (depth >= 1) initial.add(n.id);
          if (n.reports) walk(n.reports, depth + 1);
        });
      };
      walk(r.data.tree, 0);
      setCollapsed(new Set()); // fully expanded by default
    }).catch(() => {});
  }, []);

  const toggle = (id) => setCollapsed(prev => {
    const s = new Set(prev);
    s.has(id) ? s.delete(id) : s.add(id);
    return s;
  });

  const expandAll = () => setCollapsed(new Set());
  const collapseAllExceptRoot = () => {
    const s = new Set();
    const walk = (nodes) => nodes.forEach(n => { s.add(n.id); (n.reports || []).forEach(c => walk([c])); });
    tree.forEach(root => (root.reports || []).forEach(c => walk([c])));
    setCollapsed(s);
  };

  // Build a filtered tree by pruning branches with no match
  const filteredTree = useMemo(() => {
    if (!q.trim()) return tree;
    const needle = q.toLowerCase();
    const matches = (n) =>
      n.name.toLowerCase().includes(needle) ||
      (n.designation || "").toLowerCase().includes(needle) ||
      (n.department || "").toLowerCase().includes(needle);

    const prune = (n) => {
      const kept = (n.reports || []).map(prune).filter(Boolean);
      if (matches(n) || kept.length > 0) {
        return { ...n, reports: kept };
      }
      return null;
    };
    return tree.map(prune).filter(Boolean);
  }, [tree, q]);

  const rows = useMemo(() => {
    const flat = flatten(filteredTree, 0, []);
    // hide rows whose ancestor is in `collapsed`
    const result = [];
    const collapsedAncestors = [];
    for (const item of flat) {
      // pop ancestors we're past
      while (collapsedAncestors.length && collapsedAncestors[collapsedAncestors.length - 1].level >= item.level) {
        collapsedAncestors.pop();
      }
      if (collapsedAncestors.length === 0) {
        result.push(item);
      }
      if (collapsed.has(item.node.id)) {
        collapsedAncestors.push(item);
      }
    }
    return result;
  }, [filteredTree, collapsed]);

  return (
    <div className="space-y-6" data-testid="hierarchy-root">
      <div>
        <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">Organisation</div>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Company hierarchy</h1>
        <p className="mt-1 text-sm text-slate-500">All {total} employees, mapped by reporting line.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter by name, role or department" className="pl-9 h-10 bg-white" data-testid="hierarchy-search" />
        </div>
        <div className="flex rounded-lg border border-slate-200 overflow-hidden bg-white">
          <button onClick={() => setView("list")} className={`px-3 py-1.5 flex items-center gap-1.5 text-xs font-semibold ${view === "list" ? "bg-red-50 text-[#DC2626]" : "text-slate-500"}`} data-testid="view-list"><List className="h-3.5 w-3.5" strokeWidth={2} />List</button>
          <button onClick={() => setView("chart")} className={`px-3 py-1.5 flex items-center gap-1.5 text-xs font-semibold ${view === "chart" ? "bg-red-50 text-[#DC2626]" : "text-slate-500"}`} data-testid="view-chart"><LayoutGrid className="h-3.5 w-3.5" strokeWidth={2} />Chart</button>
        </div>
        <button onClick={expandAll} className="text-xs text-[#DC2626] font-semibold hover:underline" data-testid="expand-all">Expand all</button>
        <button onClick={collapseAllExceptRoot} className="text-xs text-slate-500 font-semibold hover:underline" data-testid="collapse-all">Collapse all</button>
      </div>

      {view === "list" ? (
        <Card className="p-4 rounded-xl border-slate-200/60">
          {rows.length === 0 && <div className="p-6 text-center text-sm text-slate-500">No matches.</div>}
          {rows.map(({ node, level }) => {
            const hasChildren = (node.reports || []).length > 0;
            const isCollapsed = collapsed.has(node.id);
            return (
              <div
                key={node.id}
                className={`flex items-center gap-2 py-1.5 ${level > 0 ? "border-l-2 border-slate-100" : ""}`}
                style={{ marginLeft: level * 20, paddingLeft: level ? 12 : 0 }}
              >
                {hasChildren ? (
                  <button onClick={() => toggle(node.id)} className="h-6 w-6 grid place-items-center rounded hover:bg-slate-100 text-slate-500" data-testid={`toggle-${node.employee_code}`}>
                    {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                ) : (
                  <span className="h-6 w-6 grid place-items-center text-slate-300">·</span>
                )}
                <Link to={`/employees/${node.id}`} className="flex items-center gap-3 flex-1 min-w-0 rounded-lg hover:bg-slate-50 px-2 py-1 transition-all">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-red-100 text-[#DC2626] text-xs font-semibold">{initials(node.name)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-900 truncate">{node.name}</span>
                      {level === 0 && <Badge className="bg-red-50 text-[#DC2626] border-0 text-[10px]">CEO</Badge>}
                      {hasChildren && (
                        <span className="text-[10px] font-semibold text-slate-500 flex items-center gap-1">
                          <Users className="h-3 w-3" strokeWidth={2} />{node.report_count}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate">{node.designation} · {node.department} · {node.branch}</div>
                  </div>
                </Link>
              </div>
            );
          })}
        </Card>
      ) : (
        <Card className="p-6 rounded-xl border-slate-200/60 overflow-x-auto">
          <div className="flex flex-wrap gap-3 mb-6">
            {Object.keys(DEPT_COLORS).map(d => (
              <div key={d} className="text-xs flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: deptColor(d) }}></span>{d}</div>
            ))}
          </div>
          <div className="flex justify-center min-w-max" data-testid="hierarchy-chart">
            {filteredTree.map(root => createElement(ChartNode, { key: root.id, node: root, isRoot: true }))}
          </div>
        </Card>
      )}
    </div>
  );
}

function ChartNode({ node, isRoot }) {
  const color = ({
    "Engineering": "#DC2626", "Infrastructure & Cloud": "#0EA5E9",
    "Human Resources": "#8B5CF6", "Sales": "#F59E0B", "Presales": "#10B981",
    "Finance": "#6366F1", "Support & NOC": "#EC4899",
  })[node.department] || "#64748B";
  return (
    <div className="flex flex-col items-center px-2">
      <Link to={`/employees/${node.id}`} className="group">
        <div
          className="rounded-xl border-2 bg-white shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all p-3 min-w-[180px] max-w-[200px] text-center"
          style={{ borderColor: color }}
        >
          <div className="mx-auto h-10 w-10 rounded-full grid place-items-center text-white text-xs font-bold mb-2" style={{ background: color }}>
            {node.name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()}
          </div>
          <div className="text-sm font-semibold text-slate-900 truncate group-hover:text-[#DC2626]">{node.name}</div>
          <div className="text-[11px] text-slate-500 truncate">{node.designation}</div>
          <div className="text-[10px] font-semibold uppercase tracking-wider mt-1" style={{ color }}>{node.department}</div>
          {isRoot && <div className="text-[9px] bg-red-50 text-[#DC2626] font-semibold px-2 py-0.5 rounded mt-1 inline-block">CEO</div>}
        </div>
      </Link>
      {(node.reports || []).length > 0 && (
        <>
          <div className="h-6 w-px bg-slate-300 mt-1"></div>
          <div className="flex items-start justify-center relative">
            {node.reports.length > 1 && (
              <div className="absolute top-0 left-0 right-0 h-px bg-slate-300"
                   style={{ left: `calc((100% / ${node.reports.length}) / 2)`, right: `calc((100% / ${node.reports.length}) / 2)` }}></div>
            )}
            {node.reports.map(child => (
              <div key={child.id} className="flex flex-col items-center">
                <div className="h-6 w-px bg-slate-300"></div>
                {createElement(ChartNode, { node: child })}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
